import * as React from 'react';
import { useMemo } from 'react';
import i18next from 'i18next';
import { Link } from 'react-router-dom';
import { ThemesService } from 'config/services/themes.service';
import { THEME_COLORS, subthemeColor } from 'constants/chartColors';
import { useActionTotals } from 'app/hooks/useActionTotals';
import { ChartData, useChartData } from 'app/hooks/useChartData';
import { useCachedResource } from 'app/hooks/useCachedResource';
import StatsSummary from '~components/StatsSummary';
import CountryMapChart from '../CountryMapChart';
import TerritoryBarChart from '../TerritoryBarChart';
import ThemeDonutChart, { DonutItem } from '../ThemeDonutChart';
import styles from './styles.module.scss';
import '../../i18n';

interface Props {
  /** The Fuente of the tab; `null` for "Plan Maestro" (every Acción). */
  sourceId: number | null;
}

interface Theme {
  id: number;
  subthemes: { id: number; name: string }[];
}

const themesCache = new Map<string, Theme[]>();
const themesService = new ThemesService();

const themeItems = (chart: ChartData): DonutItem[] => chart.themeCountChart.map((theme, index) => ({
  key: theme.id, label: theme.name, count: theme.count, color: THEME_COLORS[index % THEME_COLORS.length]
}));

// A Fuente tab groups its Subtemas into chart categories; a category takes its first Subtema's color.
const categoryItems = (chart: ChartData, themes: Theme[]): DonutItem[] => {
  const order = themes.flatMap(theme => theme.subthemes.map(subtheme => subtheme.id));
  return chart.categoryChart.map(item => ({
    key: item.key,
    label: item.name,
    count: item.count,
    color: subthemeColor(Math.max(0, order.indexOf(item.subtheme)))
  }));
};

/** The Home dashboard of one tab: the summary bar and the charts by país, territorio and tema/subtema. */
const SourceDashboard = ({ sourceId }: Props) => {
  const params = { source: sourceId };
  const totals = useActionTotals(params);
  const chart = useChartData(params);
  const themes = useCachedResource(themesCache, 'themes', () => themesService.get(), []);

  const counts = useMemo(() => {
    const byCode: Record<string, number> = {};
    chart?.countryChart.codes.forEach((code, i) => {
      if (code) {
        byCode[code] = chart.countryChart.data[i];
      }
    });
    return byCode;
  }, [chart]);

  const countryLabel = chart ? chart.countryChart.labels
    .map((name, i) => `${name} ${chart.countryChart.data[i]}`).join(', ') : '';
  const donutItems = chart ? (sourceId === null ? themeItems(chart) : categoryItems(chart, themes || [])) : [];

  return (
    <div className={styles.dashboard}>
      <StatsSummary variant="bar" totals={totals} />
      {chart && chart.total === 0 && <p className={styles.empty}>{i18next.t('home:noActions')}</p>}
      <div className={styles.charts} aria-busy={chart === null}>
        <section className={styles.card}>
          <h3 className={styles.title}>{i18next.t('home:byCountry')}</h3>
          {chart && <CountryMapChart counts={counts} label={countryLabel} />}
        </section>
        <section className={styles.card}>
          <h3 className={styles.title}>{i18next.t('home:byTerritory')}</h3>
          {chart && <TerritoryBarChart territories={chart.territoryChart} total={chart.total} />}
        </section>
        <section className={styles.card}>
          <h3 className={styles.title}>{i18next.t(sourceId === null ? 'home:byTheme' : 'home:bySubtheme')}</h3>
          {chart && <ThemeDonutChart items={donutItems} />}
        </section>
      </div>
      {/* Provisional look: the client is defining it with the designer. */}
      <p className={styles.seeMore}>
        <Link to="/territories/charts">{i18next.t('home:seeMore')}</Link>
      </p>
    </div>
  );
};

export default SourceDashboard;
